import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  showCloseButton?: boolean;
  footer?: React.ReactNode;
}

export default function Modal({
  isOpen,
  onClose,
  title,
  children,
  size = 'md',
  showCloseButton = true,
  footer,
}: ModalProps) {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const sizeStyles = {
    sm: 'max-w-md',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
    full: 'max-w-full mx-4',
  };

  // Portal to <body>: a transformed/overflow-hidden ancestor (page cards use
  // `transform` hover effects) would otherwise trap and clip this fixed overlay
  return createPortal(
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-brand-950/55 backdrop-blur-[1px] transition-opacity"
        onClick={onClose}
      ></div>

      {/* Modal - bottom sheet on mobile, centered dialog on desktop */}
      <div className="flex min-h-full items-end lg:items-center justify-center p-3 lg:p-4">
        <div
          className={`relative bg-white rounded-t-xl lg:rounded-lg shadow-panel border border-ink-200 ${sizeStyles[size]} w-full max-h-[85vh] lg:max-h-none overflow-y-auto animate-slide-up lg:animate-none`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          {(title || showCloseButton) && (
            <div className="flex items-center justify-between px-4 lg:px-5 py-3.5 border-b border-ink-100 bg-ink-50/80">
              {title && (
                <h3 className="text-base font-extrabold text-ink-900">{title}</h3>
              )}
              {showCloseButton && (
                <button
                  onClick={onClose}
                  className="w-8 h-8 rounded-md text-ink-400 hover:text-ink-800 hover:bg-ink-100 transition-colors flex items-center justify-center"
                >
                  <i className="fas fa-times"></i>
                </button>
              )}
            </div>
          )}

          {/* Body */}
          <div className="px-4 lg:px-5 py-4">{children}</div>

          {/* Footer */}
          {footer && (
            <div className="px-4 lg:px-5 py-3.5 border-t border-ink-100 bg-ink-50 rounded-b-lg">
              {footer}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
